import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sparkles, Phone, Mail, MapPin } from 'lucide-react';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <header className={`fixed w-full top-0 z-50 transition-all duration-500 ${
      isScrolled 
        ? 'bg-slate-900/95 backdrop-blur-xl shadow-2xl border-b border-amber-500/20' 
        : 'bg-transparent'
    }`}>
      {/* Top Contact Bar */}
      <div className="bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600 text-slate-900 py-2 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm font-medium">
          <div className="flex items-center space-x-8">
            <div className="flex items-center space-x-2">
              <Phone className="h-3 w-3" />
              <span>+94 11 XXX XXXX</span>
            </div>
            <div className="flex items-center space-x-2">
              <Mail className="h-3 w-3" />
              <span>exports@claudesspecial.lk</span>
            </div>
            <div className="hidden lg:flex items-center space-x-2">
              <MapPin className="h-3 w-3" />
              <span>Colombo, Sri Lanka</span>
            </div>
          </div>
          <div className="hidden md:block">
            <span className="text-slate-800 font-semibold">🇱🇰 Premium Ceylon Heritage</span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/cinnamon-design-3" className="flex items-center space-x-4 group">
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-500 via-yellow-400 to-amber-600 rounded-2xl flex items-center justify-center shadow-xl group-hover:shadow-2xl transition-all duration-300 group-hover:scale-105 border border-amber-400/30">
                  <Sparkles className="h-8 w-8 text-slate-900" />
                </div>
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full flex items-center justify-center">
                  <span className="text-xs">✨</span>
                </div>
              </div>
              <div>
                <div className={`text-3xl font-black transition-colors duration-300 ${
                  isScrolled ? 'text-white' : 'text-white'
                }`}>
                  Claude's Special
                </div>
                <div className={`text-sm font-bold transition-colors duration-300 ${
                  isScrolled ? 'text-amber-400' : 'text-amber-300'
                }`}>
                  Ceylon Cinnamon Exports
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-2">
              <Link
                to="/cinnamon-design-3"
                className={`px-8 py-4 rounded-2xl text-sm font-bold transition-all duration-300 ${
                  isActive('/cinnamon-design-3') 
                    ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-900 shadow-xl' 
                    : isScrolled
                      ? 'text-white hover:bg-white/10 hover:text-amber-400'
                      : 'text-white hover:bg-white/10 hover:text-amber-300'
                }`}
              >
                Home
              </Link>
              <Link
                to="/ceylon-cinnamon-design-3"
                className={`px-8 py-4 rounded-2xl text-sm font-bold transition-all duration-300 ${
                  isActive('/ceylon-cinnamon-design-3') 
                    ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-900 shadow-xl' 
                    : isScrolled
                      ? 'text-white hover:bg-white/10 hover:text-amber-400'
                      : 'text-white hover:bg-white/10 hover:text-amber-300'
                }`}
              >
                Our Cinnamon
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`p-3 rounded-xl transition-colors duration-200 ${
                  isScrolled 
                    ? 'text-white hover:bg-white/10' 
                    : 'text-white hover:bg-white/10'
                }`}
              >
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <div className="md:hidden pb-6">
              <div className="bg-slate-800/95 backdrop-blur-xl rounded-3xl shadow-2xl p-6 space-y-3 border border-amber-500/20">
                <Link
                  to="/cinnamon-design-3"
                  onClick={() => setIsOpen(false)}
                  className={`block px-6 py-4 rounded-2xl text-base font-bold transition-colors duration-200 ${
                    isActive('/cinnamon-design-3') 
                      ? 'text-slate-900 bg-gradient-to-r from-amber-500 to-yellow-500' 
                      : 'text-white hover:text-amber-400 hover:bg-white/10'
                  }`}
                >
                  Home
                </Link>
                <Link
                  to="/ceylon-cinnamon-design-3"
                  onClick={() => setIsOpen(false)}
                  className={`block px-6 py-4 rounded-2xl text-base font-bold transition-colors duration-200 ${
                    isActive('/ceylon-cinnamon-design-3') 
                      ? 'text-slate-900 bg-gradient-to-r from-amber-500 to-yellow-500' 
                      : 'text-white hover:text-amber-400 hover:bg-white/10'
                  }`}
                >
                  Our Cinnamon
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

export default Header;